import React, { useState, useMemo, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { useFileDrop } from "@/context/FileDropContext";
import { FormulaEvaluator, getColLetter } from "@/utils/formulaEvaluator";
import { 
  FileSpreadsheet, 
  Upload, 
  RotateCcw, 
  Trash2, 
  Maximize2, 
  Minimize2, 
  Plus, 
  Search, 
  Download, 
  X, 
  Edit2, 
  Check,
  ChevronDown,
  Info
} from "lucide-react";

const SAMPLE_HEADERS = ["Product", "Price", "Quantity", "Discount", "Tax Rate", "Total Revenue"];
const SAMPLE_ROWS = [
  ["Laptop Pro", "1200", "5", "0.10", "0.08", "=SUM((B1*(1-D1))*(1+E1))*C1"],
  ["Wireless Mouse", "25", "40", "0.05", "0.08", "=SUM((B2*(1-D2))*(1+E2))*C2"],
  ["Mechanical Keyboard", "80", "15", "0.00", "0.08", "=SUM((B3*(1-D3))*(1+E3))*C3"],
  ["USB-C Hub", "45", "22", "0.15", "0.08", "=SUM((B4*(1-D4))*(1+E4))*C4"],
  ["Total Units Sold", "", "=SUM(C1:C4)", "", "", ""],
  ["Average Price", "", "", "", "", "=AVERAGE(B1:B4)"],
  ["Maximum Revenue", "", "", "", "", "=MAX(F1:F4)"],
  ["Minimum Revenue", "", "", "", "", "=MIN(F1:F4)"]
];

export function ExcelEditor() {
  const [headers, setHeaders] = useState<string[]>(SAMPLE_HEADERS);
  const [rows, setRows] = useState<string[][]>(SAMPLE_ROWS);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editingHeaderIdx, setEditingHeaderIdx] = useState<number | null>(null);
  const [headerEditValue, setHeaderEditValue] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error" | ""; message: string }>({ type: "", message: "" });
  
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>("Sheet1");
  const [sheetNames, setSheetNames] = useState<string[]>(["Sheet1"]);

  // Active cell selection & editing states
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>({ row: 0, col: 5 });
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [inPlaceEditValue, setInPlaceEditValue] = useState("");
  const [formulaBarValue, setFormulaBarValue] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize the Formula Evaluation Engine
  const evaluator = useMemo(() => {
    return new FormulaEvaluator(rows, headers);
  }, [rows, headers]);

  // Synchronize Formula Bar when cell selection changes
  useEffect(() => {
    if (selectedCell) {
      const rawVal = rows[selectedCell.row]?.[selectedCell.col] || "";
      setFormulaBarValue(rawVal);
    } else {
      setFormulaBarValue("");
    }
  }, [selectedCell, rows]);

  // Filter rows based on search query (searching computed values for maximum convenience)
  const filteredRowsWithIndices = useMemo(() => {
    return rows
      .map((row, rIdx) => {
        const computedRow = row.map((_, cIdx) => evaluator.evaluateCell(rIdx, cIdx));
        return { row, computedRow, index: rIdx };
      })
      .filter(({ computedRow }) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return computedRow.some(cell => cell.toLowerCase().includes(query));
      });
  }, [rows, searchQuery, evaluator]);

  // Handle cell raw value changes
  const handleCellChange = (rowIndex: number, colIndex: number, val: string) => {
    const updated = [...rows];
    if (updated[rowIndex]) {
      updated[rowIndex][colIndex] = val;
      setRows(updated);
    }
  };

  // Handle Formula Bar text entry
  const handleFormulaBarChange = (val: string) => {
    setFormulaBarValue(val);
    if (selectedCell) {
      handleCellChange(selectedCell.row, selectedCell.col, val);
    }
  };

  // Add a blank row
  const handleAddRow = () => {
    const newRow = Array(headers.length).fill("");
    setRows([...rows, newRow]);
    showFeedback("success", "Added a new blank row.");
  };

  // Add a new column
  const handleAddColumn = () => {
    const colLetter = getColLetter(headers.length);
    const colName = prompt("Enter new column header name:") || `Column ${colLetter}`;
    setHeaders([...headers, colName]);
    setRows(rows.map(row => [...row, ""]));
    showFeedback("success", `Added column "${colName}".`);
  };

  // Delete a column
  const handleDeleteColumn = (colIdx: number) => {
    if (headers.length <= 1) {
      showFeedback("error", "You must keep at least one column.");
      return;
    }
    if (confirm(`Are you sure you want to delete column "${headers[colIdx]}"?`)) {
      setHeaders(headers.filter((_, idx) => idx !== colIdx));
      setRows(rows.map(row => row.filter((_, idx) => idx !== colIdx)));
      if (selectedCell && selectedCell.col === colIdx) {
        setSelectedCell(null);
      } else if (selectedCell && selectedCell.col > colIdx) {
        setSelectedCell({ row: selectedCell.row, col: selectedCell.col - 1 });
      }
      showFeedback("success", "Column deleted successfully.");
    }
  };

  // Rename column header
  const startHeaderEdit = (idx: number) => {
    setEditingHeaderIdx(idx);
    setHeaderEditValue(headers[idx]);
  };

  const saveHeaderEdit = (idx: number) => {
    if (!headerEditValue.trim()) return;
    const nextHeaders = [...headers];
    nextHeaders[idx] = headerEditValue.trim();
    setHeaders(nextHeaders);
    setEditingHeaderIdx(null);
    showFeedback("success", "Column header renamed.");
  };

  // Delete a row
  const handleDeleteRow = (rowIdx: number) => {
    const updated = rows.filter((_, idx) => idx !== rowIdx);
    setRows(updated);
    if (selectedCell && selectedCell.row === rowIdx) {
      setSelectedCell(null);
    } else if (selectedCell && selectedCell.row > rowIdx) {
      setSelectedCell({ row: selectedCell.row - 1, col: selectedCell.col });
    }
    showFeedback("success", "Row deleted.");
  };

  // Clear Grid
  const handleClearGrid = () => {
    if (confirm("Are you sure you want to clear the entire grid?")) {
      setHeaders(["ID", "Name"]);
      setRows([["", ""]]);
      setSelectedCell({ row: 0, col: 0 });
      setEditingCell(null);
      showFeedback("success", "Grid cleared.");
    }
  };

  // Reset Sample
  const handleResetSample = () => {
    setHeaders(SAMPLE_HEADERS);
    setRows(SAMPLE_ROWS);
    setSelectedCell({ row: 0, col: 5 });
    setEditingCell(null);
    setActiveSheet("Sheet1");
    setSheetNames(["Sheet1"]);
    setWorkbook(null);
    showFeedback("success", "Reset grid to sample spreadsheet.");
  };

  // Load worksheet data with formula preservation
  const loadSheetData = (wb: XLSX.WorkBook, sheetName: string) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    
    const rangeRef = ws['!ref'];
    if (!rangeRef) {
      setHeaders(["A", "B"]);
      setRows([["", ""]]);
      return;
    }

    try {
      const range = XLSX.utils.decode_range(rangeRef);
      
      // Determine headers
      const loadedHeaders: string[] = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: range.s.r, c });
        const cell = ws[cellRef];
        const val = cell ? String(cell.v ?? "") : "";
        loadedHeaders.push(val || getColLetter(c));
      }

      const loadedRows: string[][] = [];
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const rowArr: string[] = [];
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c });
          const cell = ws[cellRef];
          if (cell) {
            if (cell.f) {
              rowArr.push("=" + cell.f); // Load raw Excel formulas!
            } else {
              rowArr.push(cell.v !== undefined ? String(cell.v) : "");
            }
          } else {
            rowArr.push("");
          }
        }
        loadedRows.push(rowArr);
      }

      setHeaders(loadedHeaders.length > 0 ? loadedHeaders : ["A", "B"]);
      setRows(loadedRows.length > 0 ? loadedRows : [Array(loadedHeaders.length).fill("")]);
      setSelectedCell({ row: 0, col: 0 });
    } catch (e: any) {
      // Fallback
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as any[][];
      if (data.length > 0) {
        const uploadedHeaders = data[0].map(h => String(h || ""));
        const uploadedRows = data.slice(1).map(row => row.map(cell => String(cell ?? "")));
        setHeaders(uploadedHeaders);
        setRows(uploadedRows.length > 0 ? uploadedRows : [Array(uploadedHeaders.length).fill("")]);
      } else {
        setHeaders(["ID", "Name"]);
        setRows([["", ""]]);
      }
    }
  };

  // Switch worksheets
  const handleSheetChange = (name: string) => {
    if (!workbook) return;
    setActiveSheet(name);
    loadSheetData(workbook, name);
    showFeedback("success", `Switched to sheet "${name}".`);
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames);
        
        const firstSheet = wb.SheetNames[0];
        setActiveSheet(firstSheet);
        
        loadSheetData(wb, firstSheet);
        showFeedback("success", `Loaded Excel file "${file.name}" with ${wb.SheetNames.length} sheet(s).`);
      } catch (err: any) {
        showFeedback("error", `Failed to parse Excel: ${err.message || err}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Consume globally dropped file
  const { consumeFile } = useFileDrop();
  useEffect(() => {
    const file = consumeFile();
    if (file && (file.name.toLowerCase().endsWith(".xlsx") || file.name.toLowerCase().endsWith(".xls"))) {
      parseExcelFile(file);
    }
  }, [consumeFile]);

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const name = file.name.toLowerCase();
      if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        parseExcelFile(file);
      } else {
        showFeedback("error", "Please drop a valid .xlsx or .xls file.");
      }
    }
  };

  // File Input Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseExcelFile(file);
  };

  // Export Excel keeping live formulas!
  const handleExportExcel = () => {
    try {
      const ws: XLSX.WorkSheet = {};
      
      // Set headers
      headers.forEach((header, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
        ws[cellRef] = { t: "s", v: header };
      });
      
      // Set rows
      rows.forEach((row, rIdx) => {
        row.forEach((cellVal, colIdx) => {
          const cellRef = XLSX.utils.encode_cell({ r: rIdx + 1, c: colIdx });
          if (cellVal.startsWith("=")) {
            // Write true formula cell to worksheet! Remove leading '='
            ws[cellRef] = { t: "n", f: cellVal.substring(1) };
          } else if (!isNaN(Number(cellVal)) && cellVal !== "") {
            ws[cellRef] = { t: "n", v: Number(cellVal) };
          } else {
            ws[cellRef] = { t: "s", v: cellVal };
          }
        });
      });

      // Define range
      const maxRow = rows.length;
      const maxCol = headers.length - 1;
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: maxRow, c: maxCol }
      });

      const wb = workbook || XLSX.utils.book_new();
      if (!workbook) {
        XLSX.utils.book_append_sheet(wb, ws, activeSheet);
      } else {
        wb.Sheets[activeSheet] = ws;
      }
      
      XLSX.writeFile(wb, "edited_formulas_spreadsheet.xlsx");
      showFeedback("success", "Excel spreadsheet exported successfully with formulas preserved!");
    } catch (err: any) {
      showFeedback("error", `Failed to export Excel: ${err.message || err}`);
    }
  };

  const showFeedback = (type: "success" | "error", message: string) => {
    setFeedback({ type, message });
    setTimeout(() => {
      setFeedback({ type: "", message: "" });
    }, 4500);
  };

  // Start cell editing in-place
  const startInPlaceEdit = (rIdx: number, cIdx: number) => {
    setSelectedCell({ row: rIdx, col: cIdx });
    setEditingCell({ row: rIdx, col: cIdx });
    setInPlaceEditValue(rows[rIdx]?.[cIdx] || "");
  };

  // Save cell editing from in-place editor
  const saveInPlaceEdit = () => {
    if (editingCell) {
      handleCellChange(editingCell.row, editingCell.col, inPlaceEditValue);
      setEditingCell(null);
    }
  };

  // Selected cell label helper
  const getSelectedCellLabel = () => {
    if (!selectedCell) return "None";
    return `${getColLetter(selectedCell.col)}${selectedCell.row + 1}`;
  };

  return (
    <ToolPageLayout
      title="Online Excel Grid Editor"
      description="View, edit, filter, and export Excel (.xlsx, .xls) files with live formula computations directly in your browser. Fully offline-first spreadsheet editor."
      h1="Online Excel Grid Editor"
      category="JSON / Data"
      categoryPath="/excel-editor"
      howToUse={[
        "Upload any Excel file (.xlsx, .xls), drag & drop it, or start with our sample grid.",
        "Click a cell to select it, or double-click to edit cell values and formulas directly in-place.",
        "Use the formula bar (fx) to edit calculations. Supports SUM, AVERAGE, MIN, MAX, COUNT, CONCAT, UPPER, LOWER, and IF.",
        "Formula coordinates reference standard columns (A, B, C...) and rows (1, 2, 3...) - e.g. =SUM(B1:B4) or =B1+C1.",
        "Export files back to Excel with true cell formulas preserved, compatible with Microsoft Excel and Google Sheets."
      ]}
      faq={[
        { question: "Do Excel formulas really calculate live?", answer: "Yes! Any cell starting with '=' is treated as a formula. The editor parses, resolves, and displays calculated values instantly, and updates automatically whenever cell dependencies change." },
        { question: "What functions are supported?", answer: "We support: SUM(range), AVERAGE(range), MIN(range), MAX(range), COUNT(range), CONCAT(a, b), UPPER(text), LOWER(text), and IF(condition, true_val, false_val) along with mathematical operators (+, -, *, /, and brackets)." },
        { question: "Is my data private?", answer: "Absolutely. All processing, formula evaluation, and Excel imports/exports occur entirely in RAM inside your browser. Your data never leaves your machine." }
      ]}
    >
      <div className={`space-y-6 ${isFullScreen ? "fixed inset-0 z-[9999] bg-slate-50 p-6 overflow-y-auto" : ""}`}>
        
        {/* Editor Wrapper Card with Drag-and-Drop Zone */}
        <div 
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`bg-white border-2 rounded-2xl shadow-sm overflow-hidden flex flex-col transition-all ${
            isDragOver 
              ? "border-dashed border-green-500 bg-green-50/10 scale-[0.995]" 
              : "border-slate-200"
          }`}
        >
          
          {/* Header Controls */}
          <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 bg-green-100 text-green-600 rounded">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Online Excel Grid Editor</h3>
              </div>
              <p className="text-xs text-slate-500">Edit cells, write formulas, swap sheets, or manage columns and rows in real-time with full computations.</p>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {sheetNames.length > 1 && (
                <div className="flex items-center gap-1.5 mr-2">
                  <span className="text-xs font-bold text-slate-500 uppercase flex items-center">Sheet:</span>
                  <select 
                    value={activeSheet} 
                    onChange={(e) => handleSheetChange(e.target.value)}
                    className="bg-white border border-slate-200 rounded-lg text-sm px-2.5 py-1.5 font-semibold outline-none focus:border-green-500 cursor-pointer"
                  >
                    {sheetNames.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
              )}

              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="gap-1.5 text-slate-700 hover:text-slate-900"
              >
                {isFullScreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    Exit Full Screen
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    Full Screen
                  </>
                )}
              </Button>

              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileUpload} 
                accept=".xlsx, .xls" 
                className="hidden" 
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()}
                className="gap-1.5 bg-green-50 hover:bg-green-100 text-green-700 border-green-200 hover:border-green-300"
              >
                <Upload className="w-4 h-4" />
                Upload Excel File
              </Button>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResetSample}
                className="gap-1.5 text-slate-700 hover:text-slate-900"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Sample
              </Button>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleClearGrid}
                className="gap-1.5 text-red-600 hover:bg-red-50 border-transparent hover:border-red-200"
              >
                <Trash2 className="w-4 h-4" />
                Clear Grid
              </Button>
            </div>
          </div>

          {/* Premium Excel Formula Bar */}
          <div className="bg-slate-50 border-b border-slate-200 p-2.5 flex items-center gap-3">
            <div className="bg-white border border-slate-200 rounded px-3 py-1.5 text-xs font-mono font-bold text-slate-600 select-none shadow-xs shrink-0 w-20 text-center" title="Active Cell Coordinate">
              {getSelectedCellLabel()}
            </div>
            
            <div className="text-slate-400 font-mono italic font-semibold text-sm select-none shrink-0" title="Formula Function Label">
              fx
            </div>
            
            <div className="flex-1 relative">
              <input 
                type="text"
                value={formulaBarValue}
                onChange={(e) => handleFormulaBarChange(e.target.value)}
                placeholder={selectedCell ? "Enter a value or a formula starting with '=' (e.g., =SUM(A1:B3))" : "Select a cell below to edit formulas"}
                disabled={!selectedCell}
                className="w-full bg-white border border-slate-200 rounded px-3.5 py-1.5 text-sm font-mono text-slate-800 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-400/30 shadow-xs transition-all disabled:bg-slate-100/50 disabled:text-slate-400"
              />
            </div>
          </div>

          {/* Formula Helper Cheat Sheet Drawer */}
          <div className="bg-slate-50/50 border-b border-slate-100 px-5 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[11px] text-slate-500 font-medium">
            <span className="flex items-center gap-1 text-slate-600 font-semibold uppercase tracking-wider text-[10px]">
              <Info className="w-3.5 h-3.5 text-green-500" /> Formulas Support:
            </span>
            <code className="bg-white border border-slate-200/60 px-1 py-0.5 rounded text-green-700 font-semibold">=SUM(C1:C4)</code>
            <code className="bg-white border border-slate-200/60 px-1 py-0.5 rounded text-green-700 font-semibold">=AVERAGE(B1:B4)</code>
            <code className="bg-white border border-slate-200/60 px-1 py-0.5 rounded text-green-700 font-semibold">=MAX(F1:F4)</code>
            <code className="bg-white border border-slate-200/60 px-1 py-0.5 rounded text-green-700 font-semibold">=B1*C1</code>
            <code className="bg-white border border-slate-200/60 px-1 py-0.5 rounded text-green-700 font-semibold">=IF(C1&gt;100, "Yes", "No")</code>
          </div>

          {/* Search & Add controls */}
          <div className="p-4 border-b border-slate-100 bg-white flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input 
                type="text"
                placeholder="Search calculated spreadsheet rows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm outline-none focus:bg-white focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto shrink-0">
              <Button 
                onClick={handleAddRow}
                className="flex-1 sm:flex-none gap-1 bg-slate-900 hover:bg-slate-800 text-white border-none py-2 px-4 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </Button>
              <Button 
                onClick={handleAddColumn}
                variant="outline"
                className="flex-1 sm:flex-none gap-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add Column
              </Button>
            </div>
          </div>

          {/* Feedback Toast */}
          {feedback.message && (
            <div className={`px-5 py-3 border-b text-sm transition-all flex items-center justify-between ${
              feedback.type === "success" 
                ? "bg-emerald-50 text-emerald-800 border-emerald-100" 
                : "bg-red-50 text-red-800 border-red-100"
            }`}>
              <span className="font-medium">{feedback.message}</span>
              <button onClick={() => setFeedback({ type: "", message: "" })}>
                <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          )}

          {/* Excel Grid Table Area */}
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-left border-collapse min-w-[900px] table-fixed">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  {/* Row Index Header */}
                  <th className="w-16 px-4 py-3 text-xs font-bold text-slate-400 uppercase text-center border-r border-slate-200">
                    #
                  </th>
                  
                  {/* Dynamic Column Headers with Coordinates */}
                  {headers.map((header, colIdx) => (
                    <th 
                      key={colIdx} 
                      className="px-4 py-2 border-r border-slate-200 group relative select-none"
                    >
                      {editingHeaderIdx === colIdx ? (
                        <div className="flex items-center gap-1">
                          <input 
                            value={headerEditValue}
                            onChange={(e) => setHeaderEditValue(e.target.value)}
                            onBlur={() => saveHeaderEdit(colIdx)}
                            onKeyDown={(e) => e.key === "Enter" && saveHeaderEdit(colIdx)}
                            className="bg-white border border-green-500 px-1.5 py-0.5 rounded text-sm w-full outline-none font-medium"
                            autoFocus
                          />
                          <button 
                            onClick={() => saveHeaderEdit(colIdx)}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {/* Coordinate Letter */}
                          <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider font-mono">
                            {getColLetter(colIdx)}
                          </span>
                          
                          {/* Header Display Title */}
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <span 
                              onDoubleClick={() => startHeaderEdit(colIdx)}
                              className="truncate cursor-pointer hover:text-green-600 text-sm font-bold text-slate-700"
                              title="Double-click to rename header title"
                            >
                              {header}
                            </span>
                            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                              <button 
                                onClick={() => startHeaderEdit(colIdx)}
                                className="p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded"
                                title="Rename Column Title"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              {headers.length > 1 && (
                                <button 
                                  onClick={() => handleDeleteColumn(colIdx)}
                                  className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded"
                                  title="Delete Column"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                  
                  {/* Action Column */}
                  <th className="w-24 px-4 py-3 text-xs font-bold text-slate-400 uppercase text-center">
                    Action
                  </th>
                </tr>
              </thead>
              
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredRowsWithIndices.map(({ row, computedRow, index: originalRowIndex }) => (
                  <tr key={originalRowIndex} className="hover:bg-slate-50/50 group">
                    {/* Index row */}
                    <td className="px-4 py-2 text-xs font-semibold text-slate-400 text-center bg-slate-50/50 border-r border-slate-100 font-mono">
                      {originalRowIndex + 1}
                    </td>
                    
                    {/* Cell Grid Rendering */}
                    {row.map((cellValue, colIdx) => {
                      const isCellSelected = selectedCell?.row === originalRowIndex && selectedCell?.col === colIdx;
                      const isCellEditing = editingCell?.row === originalRowIndex && editingCell?.col === colIdx;
                      const displayValue = computedRow[colIdx];
                      const isFormula = cellValue.startsWith("=");

                      return (
                        <td 
                          key={colIdx} 
                          onClick={() => setSelectedCell({ row: originalRowIndex, col: colIdx })}
                          onDoubleClick={() => startInPlaceEdit(originalRowIndex, colIdx)}
                          className={`p-1 border-r border-slate-100 transition-all select-none cursor-pointer relative ${
                            isCellSelected ? "ring-2 ring-green-500 ring-inset bg-green-50/10 z-10" : ""
                          }`}
                        >
                          {isCellEditing ? (
                            <input 
                              type="text"
                              value={inPlaceEditValue}
                              onChange={(e) => {
                                setInPlaceEditValue(e.target.value);
                                setFormulaBarValue(e.target.value);
                              }}
                              onBlur={saveInPlaceEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveInPlaceEdit();
                                if (e.key === "Escape") setEditingCell(null);
                              }}
                              className="w-full bg-white border border-green-500 px-2 py-1 text-sm rounded outline-none font-mono font-medium shadow-sm z-50 relative"
                              autoFocus
                            />
                          ) : (
                            <div className="px-2 py-1 text-sm font-medium truncate flex items-center justify-between min-h-[28px]">
                              {/* Display computed text value */}
                              <span 
                                className={`${isFormula ? "text-green-700 font-mono font-semibold" : "text-slate-800"}`}
                                title={isFormula ? `Formula: ${cellValue}` : undefined}
                              >
                                {displayValue}
                              </span>
                              
                              {/* Soft green marker for formulas */}
                              {isFormula && !isCellSelected && (
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-xs shrink-0 ml-1" title="Computed formula cell" />
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    
                    {/* Row delete action */}
                    <td className="px-4 py-2 text-center">
                      <button 
                        onClick={() => handleDeleteRow(originalRowIndex)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stats Bar and Export Footer */}
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="text-xs text-slate-500 leading-relaxed max-w-2xl">
              <div className="text-slate-700 font-semibold mb-0.5">
                Spreadsheet Sandbox: {rows.length} rows & {headers.length} columns active.
              </div>
              All formula math evaluations run instantly inside browser RAM. Formula export preserves true Excel formulas compatible with MS Excel.
            </div>
            
            <Button 
              onClick={handleExportExcel}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white border-none py-3 px-6 text-sm font-bold shadow-sm rounded-xl uppercase transition-colors shrink-0"
            >
              <Download className="w-4 h-4" />
              COMPILE & EXPORT EXCEL
            </Button>
          </div>
          
        </div>

      </div>
    </ToolPageLayout>
  );
}
